<template>
  <PartnersLayout>
    <template #header>
      <PartnersHeader
        title="공지사항"
        subtitle="파트너스센터 공지사항"
        icon="notice"
        iconColor="blue"
      />
    </template>

    <div class="px-4 lg:px-6 pb-6">
      <div class="max-w-6xl mx-auto">
        <!-- 로딩 -->
        <div v-if="loading" class="flex items-center justify-center py-20">
          <div class="w-8 h-8 border-3 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
        </div>

        <!-- 공지사항 목록 -->
        <div v-else-if="notices.length > 0" class="space-y-3">
          <div
            v-for="notice in notices"
            :key="notice.id"
            class="bg-white border border-slate-200 rounded-2xl overflow-hidden"
          >
            <!-- 헤더 (클릭하여 펼치기) -->
            <button
              @click="toggleNotice(notice.id)"
              class="w-full px-4 py-4 flex items-start gap-3 text-left hover:bg-slate-50 transition-colors"
            >
              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-2 mb-1">
                  <span v-if="notice.isPinned" class="px-2 py-0.5 bg-amber-100 text-amber-700 text-[11px] font-bold rounded flex-shrink-0">중요</span>
                  <span v-if="isNew(notice.createdAt)" class="px-2 py-0.5 bg-blue-100 text-blue-700 text-[11px] font-bold rounded flex-shrink-0">NEW</span>
                </div>
                <h3 class="text-[15px] font-bold text-slate-900 mb-1">{{ notice.title }}</h3>
                <p class="text-[12px] text-slate-400">{{ formatDate(notice.createdAt) }}</p>
              </div>
              <svg
                class="w-5 h-5 text-slate-300 transition-transform flex-shrink-0 mt-1"
                :class="{ 'rotate-180': openNotices.includes(notice.id) }"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
              </svg>
            </button>

            <!-- 내용 -->
            <div
              v-show="openNotices.includes(notice.id)"
              class="px-4 pb-4 border-t border-slate-100"
            >
              <div class="pt-4">
                <div class="text-[14px] text-slate-700 leading-relaxed prose prose-sm max-w-none" v-html="notice.content"></div>
              </div>
            </div>
          </div>
        </div>

        <!-- 공지사항 없음 -->
        <div v-else class="bg-white border border-slate-200 rounded-2xl p-12 text-center">
          <div class="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <svg class="w-8 h-8 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z"/>
            </svg>
          </div>
          <p class="text-[15px] font-medium text-slate-500">등록된 공지사항이 없습니다</p>
        </div>
      </div>
    </div>
  </PartnersLayout>
</template>

<script>
import { ref, onMounted } from 'vue'
import { collection, query, where, orderBy, getDocs } from 'firebase/firestore'
import { db } from '@/firebase/firebase'
import PartnersLayout from '@/components/partners/common/PartnersLayout.vue'
import PartnersHeader from '@/components/partners/common/PartnersHeader.vue'

export default {
  name: 'PartnerNoticeView',
  components: {
    PartnersLayout,
    PartnersHeader
  },
  setup() {
    const loading = ref(true)
    const notices = ref([])
    const openNotices = ref([])

    const fetchNotices = async () => {
      loading.value = true
      try {
        const q = query(
          collection(db, 'partnerNotices'),
          where('isActive', '==', true),
          orderBy('isPinned', 'desc'),
          orderBy('createdAt', 'desc')
        )
        const snapshot = await getDocs(q)
        notices.value = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }))

        // 고정된 공지는 기본으로 펼쳐두기
        openNotices.value = notices.value
          .filter(n => n.isPinned)
          .map(n => n.id)
      } catch (error) {
        console.error('공지사항 조회 실패:', error)
      } finally {
        loading.value = false
      }
    }

    const toggleNotice = (id) => {
      const index = openNotices.value.indexOf(id)
      if (index > -1) {
        openNotices.value.splice(index, 1)
      } else {
        openNotices.value.push(id)
      }
    }

    const isNew = (dateStr) => {
      if (!dateStr) return false
      const date = new Date(dateStr)
      const now = new Date()
      const diff = now - date
      return diff < 3 * 24 * 60 * 60 * 1000 // 3일 이내
    }

    const formatDate = (dateStr) => {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      return `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, '0')}.${String(date.getDate()).padStart(2, '0')}`
    }

    onMounted(() => {
      fetchNotices()
    })

    return {
      loading,
      notices,
      openNotices,
      toggleNotice,
      isNew,
      formatDate
    }
  }
}
</script>
